import useTeamContext from '@maxpreps/shared/contexts/TeamContext';
import { pluralize } from '@maxpreps/shared/functions/primitiveHelpers/stringHelpers';
import { useToken } from '@maxpreps/shared/redux/user/userHooks';
import Alert, {
  IAlertLink,
  LOADING_OPTION
} from '@maxpreps/shared/ui/Alert/Alert';
import { ButtonTheme } from '@maxpreps/shared/ui/Button/StyledButton';
import { useState } from 'react';
import {
  convertToSeriesHelper,
  convertToSingleGameHelper,
  deleteBulkContestHelper,
  rebuildGameStoryHelper,
  restoreBulkContestHelper
} from '../../data/schedule/schedule-data-helpers';

export const SCHEDULE_ALERT_ID = {
  none: 0,
  deleteSingle: 1,
  deleteBulk: 2,
  restoreSingle: 3,
  restoreBulk: 4,
  rebuildGameStory: 5,
  convertToSingleGame: 6,
  convertToSeries: 7
};

const getAlertProps = (alertId, contestAlias) => {
  switch (alertId) {
    case SCHEDULE_ALERT_ID.deleteSingle: {
      return {
        title: `Delete ${contestAlias}?`,
        subTitle: `This will delete the ${contestAlias} and its connection to the team schedule. Games with stats or brackets will not be deleted.`
      };
    }
    case SCHEDULE_ALERT_ID.deleteBulk: {
      return {
        title: `Delete ${pluralize(contestAlias)}?`,
        subTitle: `This will delete the ${pluralize(
          contestAlias
        )} and their connection to the team schedule. Games with stats or brackets will not be deleted.`
      };
    }
    case SCHEDULE_ALERT_ID.restoreSingle: {
      return {
        title: `Restore ${contestAlias}?`,
        subTitle: `This will add the ${contestAlias} back to the team schedule and restore its data.`
      };
    }
    case SCHEDULE_ALERT_ID.restoreBulk: {
      return {
        title: `Restore ${pluralize(contestAlias)}?`,
        subTitle: `This will add the ${pluralize(
          contestAlias
        )} back to the team schedule and restore their data.`
      };
    }
    case SCHEDULE_ALERT_ID.rebuildGameStory: {
      return {
        title: `Game Story`,
        subTitle: `Rebuild and save the game story.`
      };
    }
    case SCHEDULE_ALERT_ID.convertToSingleGame: {
      return {
        title: 'Are You Sure?',
        subTitle: `Are you sure you want to convert this matchup to a single game matchup? If you choose to also delete the games, this will remove the games from the team schedules. This change cannot be undone.`
      };
    }
    case SCHEDULE_ALERT_ID.convertToSeries: {
      return {
        title: 'Are You Sure?',
        subTitle: `Are you sure you want to convert this matchup to a series game matchup?`
      };
    }
    default:
      return null;
  }
};

const getAlertLinks = (
  alertId,
  setAlertId,
  contextualContests,
  setContextualContests,
  contests,
  setContests,
  setSelectedContests,
  isLoading,
  setIsLoading,
  teamId,
  sportSeasonId,
  allSeasonId,
  token,
  canonicalUrl,
  callback
): IAlertLink[] => {
  const cancel: IAlertLink = {
    text: 'CANCEL',
    colorScheme: ButtonTheme.Info,
    as: 'button',
    asOptions: {
      onClick: () => setAlertId(SCHEDULE_ALERT_ID.none)
    }
  };

  const wrapAsyncFn = fn => () => {
    setIsLoading(true);
    fn().finally(() => {
      setIsLoading(false);
      setAlertId(SCHEDULE_ALERT_ID.none);
      setContextualContests(null);
      if (callback) {
        callback();
      }
    });
  };

  const actions: IAlertLink[] = [];

  switch (alertId) {
    case SCHEDULE_ALERT_ID.deleteSingle:
    case SCHEDULE_ALERT_ID.deleteBulk: {
      actions.push({
        text: 'DELETE',
        as: 'button',
        asOptions: {
          onClick: wrapAsyncFn(() =>
            deleteBulkContestHelper(
              teamId,
              sportSeasonId,
              contextualContests,
              token,
              contests,
              setContests,
              setSelectedContests
            )
          )
        }
      });
      break;
    }
    case SCHEDULE_ALERT_ID.restoreSingle:
    case SCHEDULE_ALERT_ID.restoreBulk: {
      actions.push({
        text: 'RESTORE',
        as: 'button',
        asOptions: {
          onClick: wrapAsyncFn(() =>
            restoreBulkContestHelper(
              teamId,
              sportSeasonId,
              contextualContests,
              token,
              contests,
              setContests,
              setSelectedContests
            )
          )
        }
      });
      break;
    }
    case SCHEDULE_ALERT_ID.rebuildGameStory: {
      actions.push({
        text: 'REBUILD + SAVE',
        as: 'button',
        asOptions: {
          onClick: wrapAsyncFn(() =>
            rebuildGameStoryHelper(
              contextualContests[0],
              teamId,
              sportSeasonId,
              token
            )
          )
        }
      });
      break;
    }
    case SCHEDULE_ALERT_ID.convertToSingleGame: {
      actions.push({
        text: 'Convert',
        as: 'button',
        asOptions: {
          onClick: wrapAsyncFn(() =>
            convertToSingleGameHelper(
              teamId,
              sportSeasonId,
              allSeasonId,
              contextualContests[0],
              false,
              canonicalUrl,
              token
            )
          )
        }
      });
      actions.push({
        text: 'Convert and Delete Games',
        as: 'button',
        asOptions: {
          onClick: wrapAsyncFn(() =>
            convertToSingleGameHelper(
              teamId,
              sportSeasonId,
              allSeasonId,
              contextualContests[0],
              true,
              canonicalUrl,
              token
            )
          )
        }
      });
      break;
    }
    case SCHEDULE_ALERT_ID.convertToSeries: {
      actions.push({
        text: 'Convert',
        as: 'button',
        asOptions: {
          onClick: wrapAsyncFn(() =>
            convertToSeriesHelper(
              teamId,
              sportSeasonId,
              allSeasonId,
              contextualContests[0],
              canonicalUrl,
              token
            )
          )
        }
      });
      break;
    }
    default:
      return [];
  }

  if (isLoading) {
    return [LOADING_OPTION, ...actions, cancel];
  }
  return [...actions, cancel];
};

const ScheduleAlert = ({
  alertId,
  setAlertId,
  contextualContests = undefined,
  setContextualContests,
  contests,
  setContests,
  setSelectedContests,
  callback
}) => {
  const teamContext = useTeamContext();
  const token = useToken();

  const { teamId, sportSeasonId, canonicalUrl, allSeasonId } = teamContext.data;
  const { contestName } = teamContext.sportData;

  const [isLoading, setIsLoading] = useState(false);

  const closeAlert = () => setAlertId(SCHEDULE_ALERT_ID.none);

  const alertProps = getAlertProps(alertId, contestName);

  const links = getAlertLinks(
    alertId,
    setAlertId,
    contextualContests,
    setContextualContests,
    contests,
    setContests,
    setSelectedContests,
    isLoading,
    setIsLoading,
    teamId,
    sportSeasonId,
    allSeasonId,
    token,
    canonicalUrl,
    callback
  );

  return alertProps ? (
    <Alert {...alertProps} links={links} onOutClick={closeAlert} />
  ) : null;
};

export default ScheduleAlert;
